import React, { useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { InputField, InputFieldDisplayProperties } from 'rootstrap/components/forms/new-fields/input-field';
import { usePromise } from 'shared/hooks/promise';
import { FormDetailsWrapper } from 'rootstrap/components/forms/form-details-wraper';
import { useSiteConfigContext } from 'style-context';
import { IdentificationType } from 'policyholder/domain/policyholder-identification';
import { SelectField } from 'rootstrap/components/forms/new-fields/select-field';
import { Moment } from 'moment';
import { ProductModule } from 'product-modules/domain/product-module';
import { LoadingLines } from 'rootstrap/components-old/loaders/loading-lines';
import { Gender } from 'models/gender';
import { CountrySelectField } from 'rootstrap/components/forms/new-fields/extended-components/country-select-field';
import { TextField } from 'rootstrap/components/forms/new-fields/extended-components/text-field';
import { ActiveElement } from 'rootstrap/components/forms/new-fields/utils';
import { PersonalAddressData } from 'policy-issuing/personal-details/views/physical-address-section';
import { EmailField } from 'rootstrap/components/forms/new-fields/extended-components/email-field';
import { Beneficiary, BeneficiaryEntityType } from 'beneficiaries/domain/beneficiary';
import { IdTypeSelectField } from 'rootstrap/components/forms/new-fields/id-type-select-field';
import { SuccessButton } from 'rootstrap/components/button/styles';
import { Cellphone } from 'policyholder/domain/policyholder';
import { TitleSelectField } from 'rootstrap/components/forms/new-fields/unused-components/title-select-field';
import { Title } from 'general/title';
import uuid from 'uuid';
import _ from 'lodash';
import { BeneficiaryRelationshipSelectField } from 'rootstrap/components/forms/new-fields/extended-components/beneficiary-relationship-select';
import { BeneficiaryDetailsStyle, StyledBeneficiaryCol } from 'beneficiaries/styles/beneficiary-details.styles';
import { PhoneNumberField } from 'rootstrap/components/forms/new-fields/phone-number-field';
import phone from 'phone';
import { ValidationTypes } from 'rootstrap/components-old/root-schema-form/utils/validation';
import { getWording, getWordingForIdTypeHandlebars, BeneficiaryInputFields, isReadOnly } from 'site-config';
import { BeneficiaryAddressFields } from './beneficiary-address-fields';
import { DatePickerField } from 'rootstrap/components/forms/new-fields/date-picker-field';

export enum BeneficiaryDetailsInputs {
  Title = 'title',
  FirstName = 'firstName',
  LastName = 'lastName',
  Relationship = 'relationship',
  IdType = 'idType',
  IdentificationNumber = 'identificationNumber',
  IdentificationCountry = 'IdentificationCountry',
  IdentificationExpirationDate = 'identificationExpirationDate',
  Gender = 'gender',
  DateOfBirth = 'dateOfBirth',
  Email = 'email',
  Cellphone = 'cellphone',
  AddressLine1 = 'addressLine1',
  AddressLine2 = 'addressLine2',
  Suburb = 'suburb',
  City = 'city',
  Country = 'country',
  AreaCode = 'areaCode',
}

export interface BeneficiaryDetailsData {
  [BeneficiaryDetailsInputs.Title]?: Title;
  [BeneficiaryDetailsInputs.FirstName]?: string;
  [BeneficiaryDetailsInputs.LastName]?: string;
  [BeneficiaryDetailsInputs.Relationship]?: string;
  [BeneficiaryDetailsInputs.IdType]?: IdentificationType;
  [BeneficiaryDetailsInputs.IdentificationNumber]?: string;
  [BeneficiaryDetailsInputs.IdentificationCountry]?: string;
  [BeneficiaryDetailsInputs.IdentificationExpirationDate]?: Moment;
  [BeneficiaryDetailsInputs.Gender]?: Gender;
  [BeneficiaryDetailsInputs.DateOfBirth]?: Moment;
  [BeneficiaryDetailsInputs.Email]?: string;
  [BeneficiaryDetailsInputs.Cellphone]?: Cellphone;
  [BeneficiaryDetailsInputs.AddressLine1]?: string;
  [BeneficiaryDetailsInputs.AddressLine2]?: string;
  [BeneficiaryDetailsInputs.Suburb]?: string;
  [BeneficiaryDetailsInputs.City]?: string;
  [BeneficiaryDetailsInputs.Country]?: string;
  [BeneficiaryDetailsInputs.AreaCode]?: string;
}

export interface BeneficiaryPrefillValues {
  title?: Title;
  firstName?: string;
  lastName?: string;
  relationship?: string;
  email?: string;
  cellphone?: Cellphone;
  dateOfBirth?: Moment;
  gender?: Gender;
  identification?: {
    type?: IdentificationType;
    number?: string;
    country?: string;
    expirationDate?: Moment;
  };
  address?: PersonalAddressData;
}

export interface BeneficiaryDetailsProps {
  productModule?: ProductModule;
  prefillValues: BeneficiaryPrefillValues;
  setActiveElement: (params: ActiveElement) => void;
  activeElement: ActiveElement;
  beneficiary: Beneficiary | undefined;
  beneficiaries: Beneficiary[];
  setBeneficiaries: (beneficiary: Beneficiary[]) => void;
  toggle: () => void;
  isManagementContext?: boolean;
}

export const BeneficiaryDetailsSection = (params: BeneficiaryDetailsProps) => {
  const { productModule } = params;
  const { siteConfig } = useSiteConfigContext();
  const { Id, Passport } = IdentificationType;
  const { activeElement, setActiveElement, beneficiary, toggle, beneficiaries, setBeneficiaries } = params;

  // Helper to determine if fields should be read-only
  const isBeneficiaryReadOnly = isReadOnly(params.isManagementContext, siteConfig, 'beneficiaries');

  const getBeneficiaryInputFieldConfig = (fieldName: keyof BeneficiaryInputFields) => {
    if (params.isManagementContext) {
      const managementConfig = siteConfig?.inputFields?.management?.beneficiaries?.[fieldName];
      return managementConfig || siteConfig?.inputFields?.beneficiaries?.[fieldName];
    }
    return siteConfig?.inputFields?.beneficiaries?.[fieldName];
  };
  const submitButtonRef = useRef<any>();

  const isAddressEnabled =
    !!productModule?.productModuleDefinition?.settings.beneficiaries.individualBeneficiaryFields?.address
      .displayAddress;

  const isDateOfBirthRequired =
    !!productModule?.productModuleDefinition?.settings.beneficiaries.individualBeneficiaryFields?.dateOfBirth?.required;

  usePromise(async () => {
    await form.trigger();
  }, []);

  const form = useForm<Partial<BeneficiaryDetailsData>>({
    mode: 'onChange',
    defaultValues: useMemo(() => ({}), []),
  });
  form.watch();

  const execute = (formValues: BeneficiaryDetailsData) => {
    const newBeneficiaries = createOrUpdateBeneficiary({
      beneficiaries,
      beneficiary,
      formValues,
    });

    setBeneficiaries(newBeneficiaries);
    return toggle();
  };

  if (!productModule) {
    return (
      <FormDetailsWrapper siteConfig={siteConfig}>
        <LoadingLines />
      </FormDetailsWrapper>
    );
  }

  const idType = form.watch(BeneficiaryDetailsInputs.IdType);

  return (
    <BeneficiaryDetailsStyle style={{ paddingBottom: 60 }}>
      <form onSubmit={form.handleSubmit((data: BeneficiaryDetailsData) => execute(data))}>
        <StyledBeneficiaryCol sm={12}>
          <TitleSelectField
            clearable={true}
            isTouched={false}
            defaultValue={beneficiary?.title}
            prefillValue={params.isManagementContext ? params.prefillValues.title : undefined}
            name={BeneficiaryDetailsInputs.Title}
            label={getWording({ wording: getBeneficiaryInputFieldConfig('title')?.label })}
            prefillAction={
              params.isManagementContext ? getBeneficiaryInputFieldConfig('title')?.prefillAction : undefined
            }
            validators={[]}
            form={form}
            placeholder={''}
            disableScrollToElement={false}
            disableActiveElement={true}
            hideBorder={true}
            disableNextButton={true}
            hideDivider={true}
            isDisabled={isBeneficiaryReadOnly}
            displayProperties={
              {
                activeElement,
                setActiveElement,
                nextComponentName: BeneficiaryDetailsInputs.FirstName,
                index: 0,
              } as InputFieldDisplayProperties
            }
          />
        </StyledBeneficiaryCol>

        <StyledBeneficiaryCol sm={12}>
          <InputField
            isTouched={false}
            placeholder={undefined}
            defaultValue={beneficiary?.firstName}
            prefillValue={params.isManagementContext ? params.prefillValues.firstName : undefined}
            validators={[
              {
                validation: {
                  type: ValidationTypes.REQUIRED,
                },
              },
            ]}
            name={BeneficiaryDetailsInputs.FirstName}
            label={getWording({ wording: getBeneficiaryInputFieldConfig('firstName')?.label })}
            prefillAction={
              params.isManagementContext ? getBeneficiaryInputFieldConfig('firstName')?.prefillAction : undefined
            }
            form={form}
            hideBorder={true}
            disableScrollToElement={false}
            disableActiveElement={true}
            disableNextButton={true}
            hideDivider={true}
            isDisabled={isBeneficiaryReadOnly}
            displayProperties={
              {
                activeElement,
                setActiveElement,
                nextComponentName: BeneficiaryDetailsInputs.LastName,
              } as InputFieldDisplayProperties
            }
          />
        </StyledBeneficiaryCol>

        <StyledBeneficiaryCol sm={12}>
          <InputField
            isTouched={false}
            placeholder={undefined}
            hideBorder={true}
            defaultValue={beneficiary?.lastName}
            prefillValue={params.isManagementContext ? params.prefillValues.lastName : undefined}
            validators={[
              {
                validation: {
                  type: ValidationTypes.REQUIRED,
                },
              },
            ]}
            name={BeneficiaryDetailsInputs.LastName}
            label={getWording({ wording: getBeneficiaryInputFieldConfig('lastName')?.label })}
            prefillAction={
              params.isManagementContext ? getBeneficiaryInputFieldConfig('lastName')?.prefillAction : undefined
            }
            form={form}
            disableScrollToElement={false}
            disableActiveElement={true}
            disableNextButton={true}
            hideDivider={true}
            isDisabled={isBeneficiaryReadOnly}
            displayProperties={
              {
                activeElement,
                setActiveElement,
                nextComponentName: BeneficiaryDetailsInputs.Relationship,
              } as InputFieldDisplayProperties
            }
          />
        </StyledBeneficiaryCol>
        {!beneficiary?.policyholderId && (
          <StyledBeneficiaryCol sm={12}>
            <BeneficiaryRelationshipSelectField
              clearable={true}
              isTouched={false}
              defaultValue={beneficiary?.relationship}
              prefillValue={params.isManagementContext ? params.prefillValues.relationship : undefined}
              validators={[
                {
                  validation: {
                    type: ValidationTypes.REQUIRED,
                  },
                },
              ]}
              name={BeneficiaryDetailsInputs.Relationship}
              label={getWording({ wording: getBeneficiaryInputFieldConfig('relationship')?.label })}
              prefillAction={
                params.isManagementContext ? getBeneficiaryInputFieldConfig('relationship')?.prefillAction : undefined
              }
              form={form}
              hideBorder={true}
              disableScrollToElement={false}
              disableActiveElement={true}
              disableNextButton={true}
              isDisabled={isBeneficiaryReadOnly}
              hideDivider={true}
              placeholder={''}
              displayProperties={
                {
                  activeElement,
                  setActiveElement,
                  nextComponentName: BeneficiaryDetailsInputs.Cellphone,
                } as InputFieldDisplayProperties
              }
            />
          </StyledBeneficiaryCol>
        )}
        <StyledBeneficiaryCol sm={12}>
          <PhoneNumberField
            isTouched={false}
            defaultValue={{
              countryCode: phone(beneficiary?.cellphone?.number || '').countryIso2 || 'ZA',
              number: beneficiary?.cellphone?.number.replace(
                phone(beneficiary?.cellphone?.number || '').countryCode || '',
                '',
              ),
            }}
            prefillValue={
              params.isManagementContext
                ? {
                    countryCode: phone(beneficiary?.cellphone?.number || '').countryIso2 || 'ZA',
                    number: beneficiary?.cellphone?.number.replace(
                      phone(beneficiary?.cellphone?.number || '').countryCode || '',
                      '',
                    ),
                  }
                : undefined
            }
            validators={[]}
            placeholder={''}
            name={BeneficiaryDetailsInputs.Cellphone}
            label={getWording({ wording: getBeneficiaryInputFieldConfig('cellphone')?.label })}
            prefillAction={
              params.isManagementContext ? getBeneficiaryInputFieldConfig('cellphone')?.prefillAction : undefined
            }
            form={form}
            hideBorder={true}
            disableScrollToElement={false}
            disableActiveElement={true}
            disableNextButton={true}
            hideDivider={true}
            isDisabled={isBeneficiaryReadOnly}
            displayProperties={
              {
                activeElement,
                setActiveElement,
                nextComponentName: BeneficiaryDetailsInputs.Email,
              } as InputFieldDisplayProperties
            }
          />
        </StyledBeneficiaryCol>

        <StyledBeneficiaryCol sm={12}>
          <EmailField
            isTouched={false}
            placeholder={undefined}
            defaultValue={beneficiary?.email}
            prefillValue={params.isManagementContext ? params.prefillValues.email : undefined}
            validators={[]}
            name={BeneficiaryDetailsInputs.Email}
            label={getWording({ wording: getBeneficiaryInputFieldConfig('email')?.label })}
            prefillAction={
              params.isManagementContext ? getBeneficiaryInputFieldConfig('email')?.prefillAction : undefined
            }
            form={form}
            hideBorder={true}
            disableScrollToElement={false}
            disableActiveElement={true}
            disableNextButton={true}
            hideDivider={true}
            isDisabled={isBeneficiaryReadOnly}
            displayProperties={
              {
                activeElement,
                setActiveElement,
                nextComponentName: BeneficiaryDetailsInputs.DateOfBirth,
              } as InputFieldDisplayProperties
            }
          />
        </StyledBeneficiaryCol>
        <StyledBeneficiaryCol sm={12}>
          <DatePickerField
            placeholder={undefined}
            form={form}
            label={getWording({ wording: getBeneficiaryInputFieldConfig('dateOfBirth')?.label })}
            prefillAction={
              params.isManagementContext ? getBeneficiaryInputFieldConfig('dateOfBirth')?.prefillAction : undefined
            }
            name={BeneficiaryDetailsInputs.DateOfBirth}
            validators={
              isDateOfBirthRequired
                ? [
                    {
                      validation: { type: ValidationTypes.REQUIRED },
                    },
                  ]
                : []
            }
            defaultValue={beneficiary?.dateOfBirth}
            prefillValue={params.isManagementContext ? params.prefillValues.dateOfBirth : undefined}
            disableScrollToElement={false}
            disableActiveElement={true}
            hideBorder={true}
            disableNextButton={true}
            hideDivider={true}
            isTouched={false}
            isDisabled={isBeneficiaryReadOnly}
            displayProperties={
              {
                activeElement,
                setActiveElement,
                nextComponentName: BeneficiaryDetailsInputs.IdType,
              } as InputFieldDisplayProperties
            }
          />
        </StyledBeneficiaryCol>

        <StyledBeneficiaryCol sm={12}>
          <IdTypeSelectField
            isTouched={false}
            clearable={true}
            placeholder={undefined}
            defaultValue={beneficiary?.id?.type}
            prefillValue={params.isManagementContext ? params.prefillValues.identification?.type : undefined}
            validators={[]}
            name={BeneficiaryDetailsInputs.IdType}
            label={getWording({ wording: getBeneficiaryInputFieldConfig('idType')?.label })}
            prefillAction={
              params.isManagementContext ? getBeneficiaryInputFieldConfig('idType')?.prefillAction : undefined
            }
            form={form}
            isDisabled={isBeneficiaryReadOnly}
            hideBorder={true}
            disableScrollToElement={false}
            disableActiveElement={true}
            disableNextButton={true}
            hideDivider={true}
            displayProperties={
              {
                activeElement,
                setActiveElement,
                nextComponentName: BeneficiaryDetailsInputs.IdentificationNumber,
              } as InputFieldDisplayProperties
            }
          />
        </StyledBeneficiaryCol>
        {idType === Id && (
          <StyledBeneficiaryCol sm={12}>
            <InputField
              isTouched={false}
              placeholder={undefined}
              defaultValue={beneficiary?.id?.number}
              prefillValue={params.isManagementContext ? params.prefillValues.identification?.number : undefined}
              validators={[
                {
                  validation: {
                    type: ValidationTypes.ZAId,
                  },
                },
              ]}
              name={BeneficiaryDetailsInputs.IdentificationNumber}
              label={getWordingForIdTypeHandlebars({
                identificationType: IdentificationType.Id,
                wording: getBeneficiaryInputFieldConfig('identificationNumber')?.label,
              })}
              prefillAction={
                params.isManagementContext
                  ? getBeneficiaryInputFieldConfig('identificationNumber')?.prefillAction
                  : undefined
              }
              form={form}
              disableScrollToElement={false}
              disableActiveElement={true}
              disableNextButton={true}
              hideDivider={true}
              hideBorder={true}
              isDisabled={isBeneficiaryReadOnly}
              displayProperties={
                {
                  activeElement,
                  setActiveElement,
                  nextComponentName: isAddressEnabled ? BeneficiaryDetailsInputs.AddressLine1 : '',
                } as InputFieldDisplayProperties
              }
            />
          </StyledBeneficiaryCol>
        )}
        {idType === Passport && (
          <StyledBeneficiaryCol sm={12}>
            <TextField
              isTouched={false}
              placeholder={undefined}
              defaultValue={beneficiary?.id?.number}
              prefillValue={params.isManagementContext ? params.prefillValues.identification?.number : undefined}
              validators={[]}
              name={BeneficiaryDetailsInputs.IdentificationNumber}
              label={getWordingForIdTypeHandlebars({
                identificationType: IdentificationType.Passport,
                wording: getBeneficiaryInputFieldConfig('identificationNumber')?.label,
              })}
              prefillAction={
                params.isManagementContext
                  ? getBeneficiaryInputFieldConfig('identificationNumber')?.prefillAction
                  : undefined
              }
              form={form}
              disableScrollToElement={false}
              disableActiveElement={true}
              disableNextButton={true}
              hideDivider={true}
              hideBorder={true}
              isDisabled={isBeneficiaryReadOnly}
              displayProperties={
                {
                  activeElement,
                  setActiveElement,
                  nextComponentName: BeneficiaryDetailsInputs.IdentificationCountry,
                } as InputFieldDisplayProperties
              }
            />
          </StyledBeneficiaryCol>
        )}
        {idType === Passport && (
          <StyledBeneficiaryCol sm={12}>
            <CountrySelectField
              clearable={true}
              isTouched={false}
              placeholder={undefined}
              form={form}
              label={getWording({ wording: getBeneficiaryInputFieldConfig('identificationCountry')?.label })}
              prefillAction={
                params.isManagementContext
                  ? getBeneficiaryInputFieldConfig('identificationCountry')?.prefillAction
                  : undefined
              }
              validators={[]}
              name={BeneficiaryDetailsInputs.IdentificationCountry}
              defaultValue={beneficiary?.id?.country}
              prefillValue={params.isManagementContext ? params.prefillValues.identification?.country : undefined}
              disableScrollToElement={false}
              disableActiveElement={true}
              disableNextButton={true}
              hideDivider={true}
              hideBorder={true}
              isDisabled={isBeneficiaryReadOnly}
              displayProperties={
                {
                  activeElement: {
                    elementId: BeneficiaryDetailsInputs.LastName,
                  },
                  setActiveElement,
                  nextComponentName: BeneficiaryDetailsInputs.Gender,
                } as InputFieldDisplayProperties
              }
            />
          </StyledBeneficiaryCol>
        )}
        {idType === Passport && (
          <StyledBeneficiaryCol sm={12}>
            <SelectField
              isTouched={false}
              clearable={true}
              placeholder={undefined}
              options={genderSelectOptions}
              form={form}
              label={getWording({ wording: getBeneficiaryInputFieldConfig('gender')?.label })}
              prefillAction={
                params.isManagementContext ? getBeneficiaryInputFieldConfig('gender')?.prefillAction : undefined
              }
              name={BeneficiaryDetailsInputs.Gender}
              validators={[]}
              defaultValue={beneficiary?.gender}
              prefillValue={params.isManagementContext ? params.prefillValues.gender : undefined}
              disableScrollToElement={false}
              disableActiveElement={true}
              disableNextButton={true}
              hideDivider={true}
              hideBorder={true}
              isDisabled={isBeneficiaryReadOnly}
              displayProperties={
                {
                  activeElement: {
                    elementId: BeneficiaryDetailsInputs.LastName,
                  },
                  setActiveElement,
                  nextComponentName: isAddressEnabled ? BeneficiaryDetailsInputs.AddressLine1 : '',
                } as InputFieldDisplayProperties
              }
            />
          </StyledBeneficiaryCol>
        )}

        {isAddressEnabled && <BeneficiaryAddressFields {...params} form={form} />}
        <button style={{ display: 'none' }} ref={submitButtonRef} type='submit' />
        <SuccessButton
          id='beneficiary-details-submit-button'
          disabled={!form.formState.isValid}
          siteConfig={siteConfig}
          onClick={() => submitButtonRef.current.click()}
          fullWidth={true}
        >
          {beneficiary ? 'Update' : 'Add'}
        </SuccessButton>
      </form>
    </BeneficiaryDetailsStyle>
  );
};

const createOrUpdateBeneficiary = (params: {
  beneficiary: Beneficiary | undefined;
  beneficiaries: Beneficiary[];
  formValues: BeneficiaryDetailsData;
}) => {
  const { beneficiary: incomingBeneficiary, formValues, beneficiaries } = params;

  const cellphone = formValues.cellphone;

  const beneficiary = new Beneficiary({
    beneficiaryId: incomingBeneficiary?.beneficiaryId || uuid().toString(),
    companyName: incomingBeneficiary?.companyName, // Group is not yet supported!!
    registrationNumber: incomingBeneficiary?.registrationNumber,
    policyholderId: incomingBeneficiary?.policyholderId,
    title: formValues.title,
    firstName: formValues.firstName,
    lastName: formValues.lastName,
    email: formValues.email,
    relationship: formValues.relationship,
    gender: formValues.gender,
    address: {
      line1: formValues.addressLine1 || '',
      line2: formValues.addressLine2 || '',
      suburb: formValues.suburb,
      city: formValues.city || '',
      areaCode: formValues.areaCode || '',
      country: formValues.country || '',
    },
    dateOfBirth: formValues.dateOfBirth,
    entityType: BeneficiaryEntityType.Individual, // No group policies handled as of yet
    cellphone: cellphone?.number
      ? {
          country: cellphone.country,
          number: cellphone.internationalNumber || cellphone.number,
        }
      : undefined,
    percentage: beneficiaries.length === 0 ? 100 : incomingBeneficiary?.percentage || 0,
    id: formValues.identificationNumber
      ? {
          type: formValues.idType || IdentificationType.Id, // We need to test that the idType field correctly updates this
          country: formValues.IdentificationCountry || 'ZA',
          number: formValues.identificationNumber || '',
        }
      : undefined,
  });

  if (incomingBeneficiary) {
    const beneficiaryIsAtIndex = beneficiaries.findIndex(
      ({ beneficiaryId }) => beneficiaryId === incomingBeneficiary.beneficiaryId,
    );
    _.set(beneficiaries, `[${beneficiaryIsAtIndex}]`, beneficiary);
    return beneficiaries;
  }

  beneficiaries.push(beneficiary);
  return beneficiaries;
};

const genderSelectOptions = [
  {
    label: 'Male',
    value: Gender.Male,
  },
  {
    label: 'Female',
    value: Gender.Female,
  },
];
